import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import CoTrader from "./CoTrader";
import CoTraderStocks from "./CoTraderStocks";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import CoTraderCombinedFilter from "./CoTraderCombinedFilter";
import "./CoTrader.css";
import {
  DeskSkeleton,
  FilterBarSkeleton,
  TickerTapeSkeleton,
} from "./CoTraderSkeletons";
import {
  DEFAULT_FX_TRADE_TYPE,
  EMPTY_TICKER_UNIVERSE,
  FX_ASSET_TYPE,
  fetchTickerUniverse,
  findSelectionForQuoteSymbol,
  findSelectionForTicker,
  flattenAssetOptions,
  getFxRuntimeSymbol,
  getAssetTypeLabel,
  resolveTickerUniverseSelection,
} from "./TickerUniverseOptions";

const COMBINED_SELECTION_STORAGE_KEY = "cotrader_combined_selection";
const COMBINED_CHART_INTERVAL_OPTIONS = [
  { value: "1m", label: "1m" },
  { value: "5m", label: "5m" },
  { value: "15m", label: "15m" },
  { value: "1h", label: "1h" },
  { value: "1d", label: "1d" },
  { value: "1wk", label: "1wk" },
  { value: "1mo", label: "1mo" },
];
const COMBINED_CHART_PERIOD_OPTIONS = [
  { value: "1d", label: "1d" },
  { value: "5d", label: "5d" },
  { value: "1mo", label: "1mo" },
  { value: "3mo", label: "3mo" },
  { value: "6mo", label: "6mo" },
  { value: "1y", label: "1y" },
  { value: "5y", label: "5y" },
  { value: "max", label: "max" },
];

const getStoredSelection = () => {
  try {
    return JSON.parse(localStorage.getItem(COMBINED_SELECTION_STORAGE_KEY) || "null");
  } catch (error) {
    console.warn("Failed to load combined CoTrader selection", error);
    return null;
  }
};

// `instrumentContext` is optional: an fdc3.instrumentList (partner embeds) whose
// first ticker selects the instrument once the ticker universe has loaded.
const CoTraderCombined = ({ instrumentContext = null }) => {
  const [tickerUniverse, setTickerUniverse] = useState(EMPTY_TICKER_UNIVERSE);
  const [isUniverseLoading, setIsUniverseLoading] = useState(true);
  const [universeError, setUniverseError] = useState("");
  const [chartInterval, setChartInterval] = useState("1m");
  const [chartPeriod, setChartPeriod] = useState("5d");
  const [toolbarPortalTarget, setToolbarPortalTarget] = useState(null);
  const [selection, setSelection] = useState(
    () =>
      getStoredSelection() || {
        assetType: "",
        country: null,
        category: null,
        tradeType: DEFAULT_FX_TRADE_TYPE,
        value: "",
      },
  );

  useEffect(() => {
    let isMounted = true;

    const loadTickerUniverse = async () => {
      setIsUniverseLoading(true);
      setUniverseError("");

      try {
        const nextTickerUniverse = await fetchTickerUniverse();
        if (!isMounted) return;
        setTickerUniverse(nextTickerUniverse);
      } catch (error) {
        if (error?.status === 401) {
          window.location.href = "/logout";
          return;
        }

        if (!isMounted) return;
        setTickerUniverse(EMPTY_TICKER_UNIVERSE);
        setUniverseError(error?.message || "Ticker universe is unavailable.");
      } finally {
        if (isMounted) {
          setIsUniverseLoading(false);
        }
      }
    };

    void loadTickerUniverse();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const ticker = instrumentContext?.instruments?.[0]?.id?.ticker;
    if (!ticker || isUniverseLoading) return;

    const nextSelection = findSelectionForTicker(tickerUniverse.options, ticker);
    if (nextSelection) {
      setSelection((previousSelection) => ({
        ...previousSelection,
        ...nextSelection,
      }));
    }
  }, [instrumentContext, isUniverseLoading, tickerUniverse.options]);

  const resolvedSelection = useMemo(
    () =>
      resolveTickerUniverseSelection({
        options: tickerUniverse.options,
        defaultSelection: tickerUniverse.defaultSelection,
        selection,
      }),
    [selection, tickerUniverse.defaultSelection, tickerUniverse.options],
  );
  const isFxAsset = resolvedSelection.assetType === FX_ASSET_TYPE;
  const showChartControls =
    Boolean(resolvedSelection.option) &&
    (!isFxAsset || resolvedSelection.tradeType === "FX_SPOT");
  const scopedAssetOptions = useMemo(
    () => flattenAssetOptions(tickerUniverse.options, resolvedSelection.assetType),
    [resolvedSelection.assetType, tickerUniverse.options],
  );
  // Depends on the asset class only, so changing pair/instrument/category
  // keeps the same list and the tape (and its socket) is left alone.
  const tickerTapeSymbols = useMemo(
    () =>
      Array.from(
        new Set(scopedAssetOptions.map((option) => option.quote_symbol).filter(Boolean)),
      ),
    [scopedAssetOptions],
  );
  const tickerTapeLabels = useMemo(
    () =>
      scopedAssetOptions.reduce((labels, option) => {
        if (option.quote_symbol) {
          labels[option.quote_symbol] = option.label || option.quote_symbol;
        }
        return labels;
      }, {}),
    [scopedAssetOptions],
  );
  const scopedStockOptions = useMemo(() => {
    if (isFxAsset || !resolvedSelection.assetType) {
      return {};
    }

    return tickerUniverse.options[resolvedSelection.assetType] || {};
  }, [isFxAsset, resolvedSelection.assetType, tickerUniverse.options]);

  useEffect(() => {
    localStorage.setItem(
      COMBINED_SELECTION_STORAGE_KEY,
      JSON.stringify({
        assetType: resolvedSelection.assetType,
        country: resolvedSelection.country,
        category: resolvedSelection.category,
        tradeType: resolvedSelection.tradeType,
        value: resolvedSelection.value,
      }),
    );

    if (isFxAsset) {
      localStorage.setItem("default_symbol", getFxRuntimeSymbol(resolvedSelection.value));
      localStorage.setItem(
        "cotrader_combined_trade_type",
        resolvedSelection.tradeType || DEFAULT_FX_TRADE_TYPE,
      );
      return;
    }

    if (resolvedSelection.country) {
      localStorage.setItem("default_country_stock", resolvedSelection.country);
    }
    if (resolvedSelection.category) {
      localStorage.setItem("default_category_stock", resolvedSelection.category);
    }
    if (resolvedSelection.value) {
      localStorage.setItem("default_symbol_stock", resolvedSelection.value);
    }
  }, [isFxAsset, resolvedSelection]);

  const handleTickerTapeSelection = (quoteSymbol) => {
    const nextSelection = findSelectionForQuoteSymbol(
      tickerUniverse.options,
      resolvedSelection.assetType,
      quoteSymbol,
    );

    if (!nextSelection) {
      return;
    }

    setSelection((previousSelection) => ({
      ...previousSelection,
      ...nextSelection,
    }));
  };

  const renderEmbeddedProduct = () => {
    // The toolbar portal target lives in the filter bar, which mounts once
    // the universe loads; hold the skeleton until it exists so the product's
    // toolbar never flashes inline.
    if (isUniverseLoading || !toolbarPortalTarget) {
      return <DeskSkeleton />;
    }

    if (!resolvedSelection.assetType || !resolvedSelection.option) {
      return (
        <div className="ct-empty-state">
          {universeError || "No eligible CoTrader instruments are available for this user."}
        </div>
      );
    }

    if (isFxAsset) {
      return (
        <CoTrader
          availableSymbolsOverride={scopedAssetOptions}
          chartIntervalOverride={chartInterval}
          chartPeriodOverride={chartPeriod}
          hideTopChrome
          hideSelectionSummary
          instrumentTypeOverride={resolvedSelection.tradeType}
          selectedSymbolOverride={getFxRuntimeSymbol(resolvedSelection.value)}
          toolbarPortalTarget={toolbarPortalTarget}
        />
      );
    }

    return (
      <CoTraderStocks
        assetTypeLabelOverride={getAssetTypeLabel(resolvedSelection.assetType)}
        chartIntervalOverride={chartInterval}
        chartPeriodOverride={chartPeriod}
        defaultSymbolOverride={resolvedSelection.value}
        hideTopChrome
        hideSelectionSummary
        selectedCategoryLabelOverride={resolvedSelection.category || ""}
        selectedCountryLabelOverride={resolvedSelection.country || ""}
        selectedSymbolOverride={resolvedSelection.value}
        selectedTickerLabelOverride={resolvedSelection.option.label || ""}
        stockOptionsOverride={scopedStockOptions}
        stockUniverseErrorOverride={universeError}
        stockUniverseLoadingOverride={isUniverseLoading}
        toolbarPortalTarget={toolbarPortalTarget}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>CoTrader | EMAlpha</title>
      </Helmet>
      <div
        className="ct-page w-100 h-100 d-flex flex-column"
        style={{ minHeight: 0 }}
      >
        <div className="px-3 pt-3 pb-2">
          <div className="ct-chrome px-3 py-2">
            {isUniverseLoading ? (
              <TickerTapeSkeleton />
            ) : universeError || tickerTapeSymbols.length === 0 ? (
              <div className="ct-chrome-status is-error">
                {universeError || "Ticker tape unavailable."}
              </div>
            ) : (
              <CoTraderCustomTickerTape
                symbols={tickerTapeSymbols}
                symbolLabels={tickerTapeLabels}
                onSymbolClick={handleTickerTapeSelection}
              />
            )}
          </div>
        </div>

        <div className="px-3 pb-2">
          <div className="ct-chrome px-3 py-2">
            {isUniverseLoading ? (
              <FilterBarSkeleton />
            ) : (
              <CoTraderCombinedFilter
                chartInterval={chartInterval}
                chartIntervalOptions={COMBINED_CHART_INTERVAL_OPTIONS}
                chartPeriod={chartPeriod}
                chartPeriodOptions={COMBINED_CHART_PERIOD_OPTIONS}
                onChartIntervalChange={setChartInterval}
                onChartPeriodChange={setChartPeriod}
                showChartControls={showChartControls}
                universeOptions={tickerUniverse.options}
                selection={resolvedSelection}
                onSelectionChange={setSelection}
                topRightContent={
                  <div
                    ref={setToolbarPortalTarget}
                    className="d-flex align-items-center justify-content-end gap-2 flex-nowrap"
                  />
                }
                isDisabled={isUniverseLoading || Boolean(universeError)}
              />
            )}
          </div>
        </div>

        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          {renderEmbeddedProduct()}
        </div>
      </div>
    </>
  );
};

export default CoTraderCombined;
